import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Response } from '../types';
import { createUniqueHash } from '../utils/FormFunctions';
import { getCurrentTimestamp } from '../utils/dateTimeFunctions';
import { saveResponse, saveAppliances } from '../utils/dbFunctions';

interface ResponseState {
    responses: Response[];
    setResponses: (responses: Response[]) => void;
    addResponse: (response: Response) => void;
    removeAllResponses: () => void;

    hasChanges: boolean;
    setHasChanges: (hasChanges: boolean) => void;

    sectionsCompleted: string[];
    addSectionCompleted: (section: string) => void;
    removeSectionCompleted: (section: string) => void;
}

// Define the store with persist middleware
export const useResponsesStore = create<ResponseState>()(
    persist(
        (set) => ({
            responses: [
                {
                    id: 'hash',
                    value: createUniqueHash(),
                },
                {
                    id: 'start_time',
                    value: getCurrentTimestamp(),
                },
                {
                    id: 'last_updated',
                    value: getCurrentTimestamp(),
                }
            ], // Initialize with a default response
            setResponses: (responses) => set({ responses }), 
            addResponse: async (response) => {
                console.log('addResponse', response);

                set((state) => {
                    // Optimistically update the state
                    const exists = state.responses.some((r) => r.id === response.id);

                    let updatedResponses = exists
                        ? state.responses.map((r) => (r.id === response.id ? response : r))
                        : [...state.responses, response];
            
                    updatedResponses = updatedResponses.map((r) =>
                        r.id === 'last_updated' ? { ...r, value: getCurrentTimestamp() } : r
                    );
            
                    return { 
                        responses: updatedResponses, 
                        hasChanges: true,
                    };
                });
            
                // Perform the asynchronous database update in the background
                setTimeout(() => {
                    const state = useResponsesStore.getState();
                    const allResponses = [...state.responses]; // Access the current state
            
                    if (response.id === 'appliances') {
                        const hash = allResponses.find((r) => r.id === 'hash')?.value;
                        saveAppliances(hash, response.value)
                            .catch((error) => {
                                console.error('Failed to save appliances:', error);
                            });
                    } else {
                        saveResponse(response, allResponses)
                            .catch((error) => {
                                console.error('Failed to save response:', error);
                            });
                    }
                }, 0); // Push the save operation to the event loop for async execution
            },
            removeAllResponses: () => set({ 
                responses: [
                    {
                        id: 'hash',
                        value: createUniqueHash(),
                    },
                    {
                        id: 'start_time',
                        value: getCurrentTimestamp(),
                    },
                    {
                        id: 'last_updated',
                        value: getCurrentTimestamp(),
                    }
                ]
            }),

            hasChanges: false,
            setHasChanges: (hasChanges) => set({ hasChanges }),

            sectionsCompleted: [],
            addSectionCompleted: (section) => set((state) => ({ sectionsCompleted: [...state.sectionsCompleted, section] })),
            removeSectionCompleted: (section) => set((state) => ({ sectionsCompleted: state.sectionsCompleted.filter((p) => p !== section) })),
        }),
        { name: 'responses' }
    )
);

export const responsesStore = useResponsesStore;